import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command as CommandPrimitive } from 'cmdk';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import {
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '../ui/command';
import {
  FileText,
  ShoppingCart,
  Target,
  Users,
  UserCircle,
  Search,
  TrendingUp,
  Clock,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { dataLakeAPI } from '../../lib/api';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';

const ENTITY_META = {
  opportunity: { icon: Target, color: 'text-violet-500', label: 'Opportunities', route: '/opportunities' },
  invoice:     { icon: FileText, color: 'text-blue-500', label: 'Invoices', route: '/invoices' },
  sales_order: { icon: ShoppingCart, color: 'text-emerald-500', label: 'Sales Orders', route: '/sales-orders' },
  account:     { icon: Users, color: 'text-amber-500', label: 'Accounts', route: '/accounts' },
  contact:     { icon: UserCircle, color: 'text-rose-500', label: 'Contacts', route: '/accounts' },
};

const stageBadge = (stage) => {
  if (!stage) return null;
  const s = stage.toLowerCase();
  const cls = s === 'won' ? 'bg-emerald-100 text-emerald-700'
    : s === 'lost' ? 'bg-red-100 text-red-700'
    : s === 'hold' ? 'bg-amber-100 text-amber-700'
    : 'bg-slate-100 text-slate-700';
  return <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${cls}`}>{stage}</Badge>;
};

const fmtAmount = (v, cur) => {
  if (!v) return null;
  const num = Number(v);
  if (isNaN(num)) return null;
  const prefix = cur || 'OMR';
  if (num >= 1_000_000) return `${prefix} ${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${prefix} ${(num / 1_000).toFixed(1)}K`;
  return `${prefix} ${num.toLocaleString()}`;
};

function ResultItem({ item, onSelect }) {
  const type = item.entity_type;
  const meta = ENTITY_META[type] || ENTITY_META.opportunity;
  const Icon = meta.icon;

  let title = '';
  let subtitle = '';
  let badge = null;
  let amount = null;

  switch (type) {
    case 'opportunity':
      title = item.name || '';
      subtitle = item.account_name || '';
      badge = stageBadge(item.custom_stage || item.stage);
      amount = fmtAmount(item.sale_value);
      break;
    case 'invoice':
      title = item.invoice_number || '';
      subtitle = item.account_name || '';
      badge = item.payment_state
        ? <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-700">{item.payment_state}</Badge>
        : null;
      amount = fmtAmount(item.amount_total, item.currency_id);
      break;
    case 'sales_order':
      title = item.so_number || item.name || '';
      subtitle = item.partner_name || '';
      badge = item.invoice_status
        ? <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-teal-50 text-teal-700">{item.invoice_status?.replace(/_/g, ' ')}</Badge>
        : null;
      amount = fmtAmount(item.amount_total);
      break;
    case 'account':
      title = item.name || '';
      subtitle = item.email || item.phone || '';
      amount = item.total_invoiced ? fmtAmount(item.total_invoiced) : null;
      break;
    case 'contact':
      title = item.name || '';
      subtitle = [item.email, item.account_name].filter(Boolean).join(' · ');
      break;
    default:
      title = item.name || item.id || '';
  }

  return (
    <CommandItem
      onSelect={() => onSelect(item, type)}
      className="cursor-pointer py-2.5 px-3"
      data-testid={`search-result-${type}-${item.canonical_id || item.source_record_id}`}
    >
      <Icon className={`mr-2.5 h-4 w-4 shrink-0 ${meta.color}`} />
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-sm font-medium truncate">{title}</span>
        {subtitle && <span className="text-xs text-muted-foreground truncate">{subtitle}</span>}
      </div>
      <div className="flex items-center gap-2 ml-2 shrink-0">
        {amount && <span className="text-xs font-medium text-slate-600">{amount}</span>}
        {badge}
      </div>
    </CommandItem>
  );
}

export function GlobalSearchDrawer() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState(() => {
    try { return JSON.parse(localStorage.getItem('recent_searches') || '[]'); } catch { return []; }
  });
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', down);
    // Expose open function for SearchTrigger
    window.__openGlobalSearch = () => setOpen(true);
    return () => {
      document.removeEventListener('keydown', down);
      delete window.__openGlobalSearch;
    };
  }, []);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setGrouped({}); return; }
    setLoading(true);
    try {
      const res = await dataLakeAPI.search(q, 'opportunity,invoice,sales_order,account,contact');
      const g = {};
      (res.data || []).forEach((item) => {
        const t = item.entity_type;
        if (!g[t]) g[t] = [];
        g[t].push(item);
      });
      setGrouped(g);
    } catch {
      setGrouped({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!query) { setGrouped({}); return; }
    const timer = setTimeout(() => doSearch(query), 250);
    return () => clearTimeout(timer);
  }, [query, doSearch]);

  const handleSelect = (item, type) => {
    setOpen(false);
    const entry = { query, type, title: item.name || item.invoice_number || item.so_number || '', ts: Date.now() };
    const updated = [entry, ...recentSearches.filter(r => r.title !== entry.title)].slice(0, 8);
    setRecentSearches(updated);
    localStorage.setItem('recent_searches', JSON.stringify(updated));

    const id = item.canonical_id || item.source_record_id;
    switch (type) {
      case 'opportunity': navigate(`/opportunities?id=${id}`); break;
      case 'invoice': navigate(`/invoices?search=${encodeURIComponent(item.invoice_number || '')}`); break;
      case 'sales_order': navigate(`/sales-orders?search=${encodeURIComponent(item.so_number || '')}`); break;
      case 'account': navigate(`/accounts?id=${id}`); break;
      case 'contact': navigate(`/accounts?id=${item.account_id || id}`); break;
      default: break;
    }
  };

  const totalResults = Object.values(grouped).reduce((s, arr) => s + arr.length, 0);
  const entityOrder = ['opportunity', 'invoice', 'sales_order', 'account', 'contact'];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="overflow-hidden p-0 max-w-[560px]" aria-describedby={undefined}>
        <VisuallyHidden.Root><DialogTitle>Search</DialogTitle></VisuallyHidden.Root>
        <CommandPrimitive
          shouldFilter={false}
          className="flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
        >
          <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <CommandPrimitive.Input
              placeholder="Search opportunities, invoices, orders, accounts..."
              value={query}
              onValueChange={setQuery}
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
              data-testid="global-search-input"
            />
            {loading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600 shrink-0" />}
          </div>

          <CommandList className="max-h-[420px]">
            {query.length >= 2 && totalResults === 0 && !loading && (
              <CommandEmpty>
                <div className="flex flex-col items-center py-6 text-muted-foreground">
                  <Search className="h-8 w-8 mb-2 opacity-30" />
                  <p className="text-sm">No results for "{query}"</p>
                  <p className="text-xs mt-1">Try a different keyword or check your spelling</p>
                </div>
              </CommandEmpty>
            )}

            {query.length < 2 && recentSearches.length > 0 && (
              <CommandGroup heading="Recent">
                {recentSearches.slice(0, 5).map((r, i) => (
                  <CommandItem key={`recent-${i}`} onSelect={() => setQuery(r.query || r.title)} className="cursor-pointer">
                    <Clock className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm">{r.title}</span>
                    {r.type && ENTITY_META[r.type] && (
                      <span className="ml-auto text-xs text-muted-foreground">{ENTITY_META[r.type].label}</span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {query.length < 2 && recentSearches.length === 0 && (
              <div className="py-10 text-center text-muted-foreground">
                <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Search across your entire CRM</p>
                <p className="text-xs mt-1">Opportunities, invoices, sales orders, accounts, contacts</p>
              </div>
            )}

            {entityOrder.map((entityKey) => {
              const items = grouped[entityKey];
              if (!items || items.length === 0) return null;
              const meta = ENTITY_META[entityKey];
              return (
                <CommandGroup key={entityKey} heading={meta.label} data-testid={`search-group-${entityKey}`}>
                  {items.map((item, idx) => (
                    <ResultItem
                      key={item.canonical_id || item.source_record_id || `${entityKey}-${idx}`}
                      item={item}
                      onSelect={handleSelect}
                    />
                  ))}
                </CommandGroup>
              );
            })}
          </CommandList>

          <div className="border-t px-3 py-2 text-xs text-muted-foreground flex items-center justify-between">
            {totalResults > 0 ? (
              <span>{totalResults} result{totalResults !== 1 ? 's' : ''} across {Object.keys(grouped).length} module{Object.keys(grouped).length !== 1 ? 's' : ''}</span>
            ) : (
              <span>Search across entire CRM</span>
            )}
            <div className="flex items-center gap-1.5">
              <kbd className="inline-flex h-5 items-center rounded border bg-muted px-1.5 font-mono text-[10px] font-medium">Esc</kbd>
              <span>to close</span>
            </div>
          </div>
        </CommandPrimitive>
      </DialogContent>
    </Dialog>
  );
}

export function SearchTrigger() {
  const handleClick = () => {
    if (window.__openGlobalSearch) window.__openGlobalSearch();
  };
  return (
    <button
      onClick={handleClick}
      data-testid="global-search-trigger"
      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-500 shadow-sm hover:bg-slate-50 hover:border-slate-300 transition-colors"
    >
      <Search className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">Search...</span>
      <kbd className="hidden sm:inline-flex h-5 items-center rounded border bg-slate-100 px-1.5 font-mono text-[10px] font-medium text-slate-400 ml-2">
        ⌘K
      </kbd>
    </button>
  );
}
